import React, { useEffect, useState } from 'react';
import { Download, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
// Brand Colors
const COLORS = {
  deepPurple: '#1a0647',
  piedmontPurple: '#8528e2',
  brightPurple: '#b063ff',
  royalBlue: '#214ae9',
  cyan: '#01ced1',
  bgLight: '#f8f8f8',
  bgWarm: '#eae6dc'
};
const DROP_URL = 'https://consumer.drop.privacy.ca.gov';
const COMPLAINT_URL = 'https://privacy.ca.gov/submit-a-complaint/ccpa-complaints/';
const CAVEO_URL = 'https://caveo.com';
const PDF_URL = '/Caveo_California_Privacy_Guide.pdf'; // Placeholder path
export function CAGuidePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen w-full bg-white font-sans text-[#1a0647]" data-id="element-567">
      {/* Font Imports (if not already in index.css) */}
      <style data-id="element-568">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@400;500;600;700;800&display=swap');
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
      `}</style>

      {/* Sticky Header */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm" data-id="element-569">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-570">
          {/* Left: Logo + Back link */}
          <div className="flex items-center gap-5" data-id="element-571">
            <Link to="/home" data-id="element-572">
              <img src="https://cdn.magicpatterns.com/uploads/8EVxVy8dkAVEdmFcPkswyr/Caveo_2026_Logo.png" alt="Caveo" className="h-7 w-auto" data-id="element-573" />
            </Link>
            <Link to="/resources/ca-guide" className="text-gray-400 hover:text-gray-600 text-sm transition-colors hidden sm:inline-flex items-center gap-1" data-id="element-574">
              ← Back to Resources
            </Link>
          </div>

          {/* Right: Login + Get Started */}
          <div className="flex items-center gap-3" data-id="element-575">
            <a href="/login" className="text-[#111111] no-underline hover:text-[#631bb7] px-3 py-1.5 rounded-lg font-medium text-sm transition-colors" data-id="element-576">
              Login
            </a>
            <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-4 py-2 rounded-lg font-semibold text-sm no-underline hover:bg-[#521099] transition-colors shadow-sm" data-id="element-577">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Section 1: Hero */}
      <section className="bg-[#1a0647] text-white py-10 md:py-12 px-4" data-id="element-578">
        <div className="max-w-5xl mx-auto" data-id="element-579">
          <div className="flex flex-row items-center gap-4" data-id="element-580">
            {/* Copy */}
            <div className="flex-1" data-id="element-581">
              <h1 className="font-bold text-2xl md:text-3xl lg:text-4xl leading-tight text-white" data-id="element-582">
                The California Resident's Guide to Privacy & Opt-Out
              </h1>
            </div>
            {/* Image */}
            <div className="flex-shrink-0 w-24 md:w-32" data-id="element-583">
              <img src="https://cdn.magicpatterns.com/uploads/rEBPBLjGsXRNeHWosHkCNx/Gemini_Generated_Image_j7hwgij7hwgij7hw.png" alt="California Privacy" className="w-full h-auto drop-shadow-xl" data-id="element-584" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: LOCKED Framework */}
      <section className="py-8 md:py-12 px-4 max-w-7xl mx-auto" data-id="element-585">
        <div className="text-center mb-12 max-w-3xl mx-auto" data-id="element-586">
          <h2 className="font-bold text-2xl md:text-3xl text-[#1a0647] mb-6" data-id="element-587">
            Your Privacy Rights as a California Resident
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-588">
            California law gives you six core rights over your personal
            information. Businesses that collect your data — whether it's your
            shopping habits, your location, or your browsing history — are
            required to respect these rights under the California Consumer
            Privacy Act (CCPA) and the California Privacy Rights Act (CPRA).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-589">
          {[{
          letter: 'L',
          title: 'Limit',
          desc: 'You can limit how businesses use and share your sensitive personal information — like your Social Security number, health data, or precise location.'
        }, {
          letter: 'O',
          title: 'Opt-Out',
          desc: 'You can opt out of businesses selling your personal information or sharing it for targeted advertising purposes.'
        }, {
          letter: 'C',
          title: 'Correct',
          desc: 'If a company has inaccurate information about you, you have the right to ask them to fix it.'
        }, {
          letter: 'K',
          title: 'Know',
          desc: 'You have the right to know exactly what personal information a business has collected about you and how they use or share it.'
        }, {
          letter: 'E',
          title: 'Equal Treatment',
          desc: "Businesses can't penalize you, charge you more, or give you worse service just because you exercise your privacy rights."
        }, {
          letter: 'D',
          title: 'Delete',
          desc: "You can request that businesses delete the personal information they've collected from you. (Some limited exceptions apply.)"
        }].map((item, idx) => <div key={idx} className="bg-[#f8f8f8] rounded-lg p-6 shadow-sm border-l-4 border-[#b063ff] hover:shadow-md transition-shadow" data-id="element-590">
              <div className="flex items-baseline gap-3 mb-2" data-id="element-591">
                <span className="font-bold text-4xl text-[#8528e2]" data-id="element-592">
                  {item.letter}
                </span>
                <h3 className="font-bold text-xl text-[#1a0647]" data-id="element-593">
                  {item.title}
                </h3>
              </div>
              <p className="text-gray-700 leading-relaxed" data-id="element-594">{item.desc}</p>
            </div>)}
        </div>

        <p className="text-center font-inter italic text-gray-500 text-sm" data-id="element-595">
          Think of LOCKED as your privacy cheat code. Each letter is a right you
          can actually exercise today.
        </p>
      </section>

      {/* Section 3: Introducing DROP */}
      <section className="bg-[#eae6dc] py-16 md:py-24 px-4" data-id="element-596">
        <div className="max-w-7xl mx-auto" data-id="element-597">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-598">
            <h2 className="font-bold text-3xl md:text-4xl text-[#1a0647] mb-2" data-id="element-599">
              Introducing DROP
            </h2>
            <h3 className="font-bold text-xl text-[#8528e2] mb-6" data-id="element-600">
              Delete Request and Opt-Out Platform
            </h3>
            <p className="text-lg text-gray-800 leading-relaxed mb-6" data-id="element-601">
              DROP is a free, official tool from the California Privacy
              Protection Agency (CalPrivacy) that lets you send a single
              deletion request to over 500 registered data brokers at once. It
              launched on January 1, 2026, and it's exclusively available to
              California residents.
            </p>
            <p className="text-lg text-gray-800 leading-relaxed" data-id="element-602">
              Data brokers are companies you've probably never heard of that
              collect and sell your personal information — sometimes including
              your address, phone number, search history, Social Security
              number, and even data about your children. DROP gives you a way to
              fight back without having to contact each company one by one.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-603">
            {[{
            title: 'More Control',
            desc: 'Limits what data brokers collect and sell about you and your family.'
          }, {
            title: 'Less Spam',
            desc: 'When your data stops being sold, unwanted calls, texts, and emails decrease.'
          }, {
            title: 'Greater Security',
            desc: 'Reduces your risk of identity theft, fraud, AI impersonation, and data leaks.'
          }].map((card, idx) => <div key={idx} className="rounded-lg overflow-hidden border border-[#e0d8f0] shadow-sm" data-id="element-604">
                <div className="bg-[#1a0647] text-white p-4 font-bold text-lg text-center" data-id="element-605">
                  {card.title}
                </div>
                <div className="bg-[#f8f8f8] p-6 text-gray-800 text-center h-full" data-id="element-606">
                  {card.desc}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Section 2.5: Before You Start */}
      <section className="py-16 md:py-24 px-4 max-w-7xl mx-auto" data-id="element-607">
        <div className="text-center mb-12 max-w-3xl mx-auto" data-id="element-608">
          <h2 className="font-bold text-3xl md:text-4xl text-[#1a0647] mb-6" data-id="element-609">
            Before You Start: What to Gather
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-610">
            You can submit a DROP request with just the basics, but the more
            information you include, the better your chances of a thorough
            deletion. Here's what to have ready.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-611">
          {/* Left Column */}
          <div data-id="element-612">
            <h3 className="font-bold text-xl text-[#1a0647] mb-6 flex items-center gap-2" data-id="element-613">
              <span className="w-8 h-8 rounded-full bg-[#01ced1] text-white flex items-center justify-center text-sm" data-id="element-614">
                ✓
              </span>
              Ready to go (you already know these)
            </h3>
            <div className="space-y-4" data-id="element-615">
              {[{
              label: 'Full name',
              desc: "Include maiden names or any previous names you've used."
            }, {
              label: 'Date of birth',
              desc: 'Used to match your records. Only one date of birth per request.'
            }, {
              label: 'ZIP code',
              desc: 'Your current or most recent California ZIP code.'
            }, {
              label: 'Email addresses',
              desc: 'You can add multiple. Include old ones too.'
            }, {
              label: 'Phone numbers',
              desc: 'Both current and previous numbers help brokers find you.'
            }].map((item, idx) => <div key={idx} className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-4 rounded-r-lg" data-id="element-616">
                  <span className="font-bold text-[#1a0647] block mb-1" data-id="element-617">
                    {item.label}
                  </span>
                  <span className="text-gray-700 text-sm" data-id="element-618">{item.desc}</span>
                </div>)}
            </div>
          </div>

          {/* Right Column */}
          <div data-id="element-619">
            <h3 className="font-bold text-xl text-[#1a0647] mb-2 flex items-center gap-2" data-id="element-620">
              <span className="w-8 h-8 rounded-full bg-[#8528e2] text-white flex items-center justify-center text-sm" data-id="element-621">
                ★
              </span>
              Worth looking up first
            </h3>
            <p className="text-sm italic text-gray-500 mb-6" data-id="element-622">
              These three identifiers significantly increase your chances of a
              match. If you have time to find them before you start, it's worth
              it.
            </p>
            <div className="space-y-4" data-id="element-623">
              <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-4 rounded-r-lg" data-id="element-624">
                <span className="font-bold text-[#1a0647] block mb-1" data-id="element-625">
                  Mobile Advertising ID (MAID)
                </span>
                <span className="text-gray-700 text-sm block mb-2" data-id="element-626">
                  A unique number assigned to your smartphone. Find it in your
                  phone's privacy settings.
                </span>
                <ul className="list-disc pl-4 text-xs text-gray-600 space-y-1" data-id="element-627">
                  <li data-id="element-628">
                    <strong data-id="element-629">iPhone:</strong> Settings &gt; Privacy & Security
                    &gt; Tracking
                  </li>
                  <li data-id="element-630">
                    <strong data-id="element-631">Android:</strong> Settings &gt; Privacy &gt; Ads
                  </li>
                </ul>
              </div>
              <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-4 rounded-r-lg" data-id="element-632">
                <span className="font-bold text-[#1a0647] block mb-1" data-id="element-633">
                  Vehicle Identification Number (VIN)
                </span>
                <span className="text-gray-700 text-sm" data-id="element-634">
                  The 17-character number that identifies your car. Find it on
                  your dashboard, registration card, or insurance documents.
                </span>
              </div>
              <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-4 rounded-r-lg" data-id="element-635">
                <span className="font-bold text-[#1a0647] block mb-1" data-id="element-636">
                  Connected TV ID (TVID)
                </span>
                <span className="text-gray-700 text-sm" data-id="element-637">
                  A unique identifier for your smart TV. Find it in your TV's
                  Settings under Privacy, About, or Advertising.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: How to Use DROP */}
      <section className="bg-white py-16 md:py-24 px-4 max-w-5xl mx-auto border-t border-gray-100" data-id="element-638">
        <div className="text-center mb-12" data-id="element-639">
          <h2 className="font-bold text-3xl md:text-4xl text-[#1a0647] mb-6" data-id="element-640">
            How to Use DROP in 3 Steps
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto" data-id="element-641">
            It takes about 10 minutes. Once submitted, data brokers are required
            to process your request and delete your data within 90 days starting
            August 1, 2026.
          </p>
        </div>

        <div className="space-y-8 mb-12" data-id="element-642">
          {[{
          step: 1,
          title: 'Verify Your Eligibility',
          desc: "Confirm you're a California resident using the California Identity Gateway — the state's secure verification system. You don't need to create an account, and DROP won't retain your information."
        }, {
          step: 2,
          title: 'Create Your Profile',
          desc: 'Provide basic information about yourself so data brokers can locate your records. You decide how much you share. The more you include, the more thorough the deletion.'
        }, {
          step: 3,
          title: 'Submit Your DROP Request',
          desc: "With one submission, your deletion request is sent to 500+ registered data brokers simultaneously. That's it."
        }].map(item => <div key={item.step} className="flex flex-col md:flex-row gap-6 items-start bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all" data-id="element-643">
              <div className="flex-shrink-0 w-12 h-12 bg-[#8528e2] rounded-full flex items-center justify-center text-white font-bold text-xl" data-id="element-644">
                {item.step}
              </div>
              <div data-id="element-645">
                <h3 className="font-bold text-xl text-[#1a0647] mb-2" data-id="element-646">
                  {item.title}
                </h3>
                <p className="text-gray-700 leading-relaxed" data-id="element-647">{item.desc}</p>
              </div>
            </div>)}
        </div>

        <div className="bg-[#eae6dc] border-t-4 border-[#01ced1] p-8 rounded-lg mb-10 text-center" data-id="element-648">
          <p className="text-[#1a0647] font-medium" data-id="element-649">
            DROP is free. The state will never charge you to use it. You can
            also submit a DROP request on behalf of a family member — including
            a child or an elderly relative.
          </p>
        </div>

        <div className="text-center" data-id="element-650">
          <a href={DROP_URL} target="_blank" rel="noopener noreferrer" className="inline-block w-full md:w-auto bg-[#8528e2] hover:bg-[#b063ff] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-651">
            Get Started with DROP
          </a>
        </div>
      </section>

      {/* Section 4 (Expanded): After You Submit */}
      <section className="bg-[#f8f8f8] py-16 md:py-24 px-4" data-id="element-652">
        <div className="max-w-7xl mx-auto" data-id="element-653">
          <div className="text-center mb-12 max-w-3xl mx-auto" data-id="element-654">
            <h2 className="font-bold text-3xl md:text-4xl text-[#1a0647] mb-6" data-id="element-655">
              After You Submit: What to Expect
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed" data-id="element-656">
              Data brokers aren't required to start processing requests until
              August 1, 2026. Once they do, they have up to 90 days to complete
              each request. Here's how to read your DROP status when you check
              back.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-657">
            {[{
            status: 'Deleted',
            color: '#01ced1',
            desc: "Data brokers deleted your data and will no longer sell it. Some information may still be kept if it's legally exempt, like public records."
          }, {
            status: 'Opted-Out',
            color: '#8528e2',
            desc: "Brokers couldn't make an exact match, but they can no longer sell your data. Try adding more information to improve your match."
          }, {
            status: 'Exempted',
            color: '#214ae9',
            desc: 'All data this broker holds about you is legally exempt from deletion. It may still be protected under other laws.'
          }, {
            status: 'Record Not Found',
            color: '#b063ff',
            desc: "The broker couldn't find you based on what you provided. Adding more identifiers (MAID, VIN, old phone numbers) can help."
          }, {
            status: 'Pending',
            color: '#1a0647',
            desc: "Your request hasn't been processed yet. Brokers have up to 90 days from August 1, 2026."
          }].map((card, idx) => <div key={idx} className="bg-white rounded-lg p-6 shadow-sm border-l-8" style={{
            borderLeftColor: card.color
          }} data-id="element-658">
                <h3 className="font-bold text-xl mb-3" style={{
              color: card.color
            }} data-id="element-659">
                  {card.status}
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed" data-id="element-660">
                  {card.desc}
                </p>
              </div>)}
          </div>

          <div className="bg-white p-8 rounded-xl border border-gray-200 max-w-3xl mx-auto text-center" data-id="element-661">
            <h3 className="font-bold text-xl text-[#1a0647] mb-3" data-id="element-662">
              How to improve a result that isn't "Deleted"
            </h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-663">
              Log back into your DROP profile and add more identifiers. More
              data means better matches. Common additions that help: old email
              addresses, a previous last name, a former phone number, your MAID,
              or your VIN. Updated requests give brokers another 90-day
              processing window.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4.5: Tips & Tricks */}
      <section className="bg-white py-16 md:py-24 px-4 max-w-5xl mx-auto" data-id="element-664">
        <h2 className="font-bold text-3xl md:text-4xl text-[#1a0647] mb-12 text-center" data-id="element-665">
          Tips & Tricks for Getting the Most Out of DROP
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-666">
          {[{
          title: 'Use maiden names and previous names',
          desc: "Data brokers often have older records tied to names you haven't used in years. Including them dramatically increases match rates."
        }, {
          title: 'Add multiple email addresses',
          desc: 'Most people have had two or three email addresses over their lifetime. Each one is a potential match point. Include personal, work, and old accounts.'
        }, {
          title: "Don't skip the optional identifiers",
          desc: "Your MAID, VIN, and TVID are rare enough that they're nearly unique. A match on any one of these is highly likely to pull up your full profile at a data broker."
        }, {
          title: 'Save your DROP ID',
          desc: "You'll need this to check your status and update your request later. Take a screenshot or write it down somewhere safe."
        }, {
          title: 'You can submit for a family member',
          desc: 'Parents can submit on behalf of a child. Adult children can submit for an elderly parent. Just follow the in-app instructions to complete the third-party request.'
        }, {
          title: 'Opt specific brokers back in if needed',
          desc: 'If you use a service that relies on a specific data broker (like a credit monitoring service), you can uncheck that broker on the data broker list page within DROP so they keep your data while others delete it.'
        }, {
          title: 'Come back when things change',
          desc: 'Get a new phone number? Change your name? Buy a car? Log back in and add the new information. Brokers process updates within 90 days.'
        }, {
          title: 'Check your status after 90 days',
          desc: "Once brokers start processing in August 2026, give them the full 90 days before checking. If it's been longer and nothing changed, try adding more information to your profile."
        }].map((tip, idx) => <div key={idx} className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-6 rounded-r-lg hover:bg-[#f0f0f0] transition-colors" data-id="element-667">
              <h3 className="font-bold text-[#1a0647] text-lg mb-2" data-id="element-668">
                {tip.title}
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed" data-id="element-669">
                {tip.desc}
              </p>
            </div>)}
        </div>
      </section>

      {/* Section 5: Enable OOPS */}
      <section className="bg-[#f8f8f8] py-16 md:py-24 px-4" data-id="element-670">
        <div className="max-w-5xl mx-auto" data-id="element-671">
          <div className="text-center mb-12" data-id="element-672">
            <h2 className="font-bold text-3xl md:text-4xl text-[#1a0647] mb-6" data-id="element-673">
              Enable OOPS on Your Browser
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto mb-6" data-id="element-674">
              In addition to DROP, California gives you another layer of
              protection called the Opt-Out Preference Signal — or OOPS. (Yes,
              that's really what it's called, and it actually works.)
            </p>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto" data-id="element-675">
              OOPS is a setting in your browser or a browser extension that
              automatically tells websites not to sell or share your data. You
              don't have to fill out a form for every website you visit. You
              turn it on once, and it works in the background on your behalf.
            </p>
          </div>

          <h3 className="font-bold text-2xl text-[#8528e2] text-center mb-10" data-id="element-676">
            How to Turn It On
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-677">
            {[{
            step: 1,
            title: 'Choose a compatible browser or extension',
            desc: 'Use a browser with OOPS built in, like Brave or Firefox, or add a browser extension like Global Privacy Control (GPC) to Chrome or Edge.'
          }, {
            step: 2,
            title: 'Open your privacy settings',
            desc: 'Go to Settings, then find the Privacy or Security section. If you installed an extension, open its settings instead.'
          }, {
            step: 3,
            title: 'Turn on OOPS',
            desc: 'Locate the opt-out preference signal toggle and turn it on. Done. Every website you visit is automatically notified of your preference.'
          }].map(item => <div key={item.step} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center text-center" data-id="element-678">
                <div className="w-10 h-10 bg-[#8528e2] rounded-full flex items-center justify-center text-white font-bold text-lg mb-4" data-id="element-679">
                  {item.step}
                </div>
                <h4 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-680">
                  {item.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-681">
                  {item.desc}
                </p>
              </div>)}
          </div>

          <p className="text-center italic text-gray-500 text-sm max-w-2xl mx-auto" data-id="element-682">
            California passed the Opt Me Out Act (AB 566) in 2025, which will
            require all browsers to support OOPS starting January 2027. For now,
            you may need to switch browsers or install an extension.
          </p>
        </div>
      </section>

      {/* Section 6: DROP Timeline */}
      <section className="bg-white py-16 md:py-24 px-4 max-w-4xl mx-auto" data-id="element-683">
        <div className="text-center mb-12" data-id="element-684">
          <h2 className="font-bold text-3xl md:text-4xl text-[#1a0647] mb-4" data-id="element-685">
            DROP Timeline
          </h2>
          <p className="text-lg text-gray-700" data-id="element-686">
            Since DROP is brand new, it's worth knowing what to expect and when.
          </p>
        </div>

        <div className="space-y-6" data-id="element-687">
          {[{
          date: 'January 1, 2026',
          color: '#01ced1',
          desc: 'DROP launched. California residents can start submitting deletion requests now.'
        }, {
          date: 'August 1, 2026',
          color: '#8528e2',
          desc: 'Data brokers are legally required to begin processing your requests.'
        }, {
          date: 'Within 90 days of Aug 1',
          color: '#214ae9',
          desc: 'Data brokers complete processing and update your DROP status.'
        }, {
          date: 'Moving Forward',
          color: '#1a0647',
          desc: 'Data brokers must delete data on a rolling 45-day cycle going forward.'
        }].map((item, idx) => <div key={idx} className="flex bg-[#f8f8f8] rounded-r-lg overflow-hidden border-l-8" style={{
          borderLeftColor: item.color
        }} data-id="element-688">
              <div className="p-6 w-full" data-id="element-689">
                <h3 className="font-bold text-lg mb-1" style={{
              color: item.color
            }} data-id="element-690">
                  {item.date}
                </h3>
                <p className="text-[#333333]" data-id="element-691">{item.desc}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* Section 7: Compliance */}
      <section className="bg-[#eae6dc] py-16 md:py-24 px-4" data-id="element-692">
        <div className="max-w-4xl mx-auto text-center" data-id="element-693">
          <h2 className="font-bold text-3xl md:text-4xl text-[#1a0647] mb-6" data-id="element-694">
            What If a Business Doesn't Comply?
          </h2>
          <p className="text-lg text-gray-800 leading-relaxed mb-8" data-id="element-695">
            California residents can file a complaint with the California
            Privacy Protection Agency (CalPrivacy) if a business violates your
            CCPA rights or fails to honor your DROP request. Complaints are
            taken seriously. CalPrivacy is the agency specifically created to
            enforce California privacy law and protect consumers like you.
          </p>
          <a href={COMPLAINT_URL} target="_blank" rel="noopener noreferrer" className="font-bold text-[#8528e2] text-lg hover:text-[#b063ff] underline decoration-2 underline-offset-4 transition-colors" data-id="element-696">
            File a complaint at privacy.ca.gov/submit-a-complaint
          </a>
        </div>
      </section>

      {/* Section 8: Closing / Caveo Footer */}
      <section className="bg-[#1a0647] text-white py-16 md:py-24 px-4" data-id="element-697">
        <div className="max-w-4xl mx-auto text-center" data-id="element-698">
          <div className="flex justify-center mb-8" data-id="element-699">
            <img src="https://cdn.magicpatterns.com/uploads/xs4vCSoZgSfo2aygxToMWG/Caveo_Logotype_Horizontal_240x50_White_Jumpshare_PNG.png" alt="Caveo" className="h-10 w-auto" data-id="element-700" />
          </div>
          <h2 className="font-bold text-3xl mb-6 text-white" data-id="element-701">
            A Note from Caveo
          </h2>
          <div className="text-lg text-gray-300 leading-relaxed space-y-6 mb-10 max-w-3xl mx-auto" data-id="element-702">
            <p data-id="element-703">
              Privacy rights only work if you know they exist and know how to
              use them. That's the whole reason Caveo was built. We're
              California residents too, and we believe every family deserves
              straightforward, jargon-free guidance on protecting themselves
              online.
            </p>
            <p data-id="element-704">
              DROP is a genuinely powerful tool. So is OOPS. Neither requires a
              computer science degree to use. We hope this guide makes it a
              little easier to take that first step.
            </p>
          </div>
          <a href={CAVEO_URL} className="font-bold text-white text-lg hover:text-[#b063ff] underline decoration-2 underline-offset-4 transition-colors" data-id="element-705">
            Learn more at caveo.com
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#110430] text-gray-400 py-12 px-4 border-t border-white/10" data-id="element-706">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left" data-id="element-707">
          <div className="flex items-center gap-4" data-id="element-708">
            <img src="https://cdn.magicpatterns.com/uploads/xs4vCSoZgSfo2aygxToMWG/Caveo_Logotype_Horizontal_240x50_White_Jumpshare_PNG.png" alt="Caveo" className="h-6 w-auto opacity-80" data-id="element-709" />
            <span className="text-xs" data-id="element-710">
              © 2026 AppEmbrace Inc. DBA Caveo. All rights reserved.
            </span>
          </div>
          <div className="text-xs text-gray-500 max-w-md" data-id="element-711">
            Sources: California Privacy Protection Agency (privacy.ca.gov) |
            California Consumer Privacy Act (CCPA) | California Privacy Rights
            Act (CPRA) | AB 566, California Opt Me Out Act (2025)
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-white/5 text-center text-xs text-gray-600" data-id="element-712">
          This guide is an independent educational resource produced by Caveo
          (AppEmbrace Inc.) and is not affiliated with or endorsed by the State
          of California or the California Privacy Protection Agency.
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:hidden z-50 transition-transform duration-300 ${isScrolled ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-713">
        <a href={DROP_URL} target="_blank" rel="noopener noreferrer" className="block w-full bg-[#8528e2] text-white py-3 rounded-lg font-bold text-center shadow-lg" data-id="element-714">
          Use DROP Now →
        </a>
      </div>
    </div>;
}